import React from 'react'
import './privacy-and-terms.scss'
import Navbar from '../Navbar/Navbar'
import Footer from '../Footer/Footer'
import SEO from '../SEO/SEO'

const PrivacyPolicy = () => {
  return (
    <>
      <SEO
        title="Privacy Policy | Teleport Now"
        description="Read how Teleport Now (SG) Pte. Ltd. collects, uses, shares, protects, retains and deletes personal data across its website, mobile app and travel services."
        canonicalPath="/privacy-policy"
      />
      <Navbar />
      <div className="privacy-and-terms">
        <div className="container">
          <div className="header-title">
            <div className="overlay">
              <h2>Privacy Policy</h2>
              <p className="legal-hero-meta">Last Updated: <span>10 October 2025</span></p>
            </div>
          </div>

          <div className="page-content">
            <div className="term-heading">
              <h4>Privacy Policy</h4>
              <p>
                Last Updated: <span>10 October 2025</span>
              </p>
            </div>

            <div className="point-wrap">
              <hr />

              {/* 1. Introduction */}
              <h2>1. Introduction</h2>
              <p>
                This Privacy Policy (“Policy”) explains how{" "}
                <b>Teleport Now (SG) Pte. Ltd.</b> (“Teleport Now,” “we,” “us,” or “our”)
                collects, uses, discloses, transfers, and stores your personal data
                when you use our websites, mobile applications, and related
                services (“Services”).
              </p>
              <p>
                By using Teleport Now’s Services, you agree to this Policy and our
                Terms and Conditions. If you do not agree, please stop using our
                Services immediately.
              </p>

              <hr />

              {/* 2. Information We Collect */}
              <h2>2. Information We Collect</h2>
              <p>We may collect the following categories of personal data:</p>
              <ul>
                <li>
                  <b>Identity & Contact Information:</b> Name, postal address,
                  phone number, email address.
                </li>
                <li>
                  <b>Device Information:</b> Device ID, operating system, browser
                  type, mobile identifiers.
                </li>
                <li>
                  <b>Network & Location Data:</b> IP address, mobile network
                  details, GPS location (where enabled), approximate geolocation
                  based on IP.
                </li>
                <li>
                  <b>Usage Data:</b> Interactions with our app/website, browsing
                  behavior, booking history, eSIM activation, preferences (e.g.,
                  language, currency), and customer support interactions.
                </li>
                <li>
                  <b>Booking & Transaction Data:</b> Travel itineraries,
                  reservations, ticketing details, payment information, billing
                  address, and transaction records.
                </li>
                <li>
                  <b>Compliance Data:</b> Identity documents, verification details,
                  or other information required by applicable law (e.g., Know Your
                  Customer checks).
                </li>
                <li>
                  <b>Communications:</b> Customer support correspondence, surveys,
                  reviews, and feedback.
                </li>
              </ul>

              <hr />

              {/* 3. How We Collect Data */}
              <h2>3. How We Collect Data</h2>
              <p>We collect data in the following ways:</p>
              <ul>
                <li>
                  <b>Directly from you</b> – when you register, book a service,
                  purchase an eSIM, or contact support.
                </li>
                <li>
                  <b>Automatically</b> – via cookies, analytics tools, and in-app
                  tracking.
                </li>
                <li>
                  <b>From third parties</b> – including payment providers, identity
                  verification vendors, and distribution partners.
                </li>
              </ul>

              <hr />

              {/* 4. How We Use Your Information */}
              <h2>4. How We Use Your Information</h2>
              <p>We use your personal data to:</p>
              <ol>
                <li>
                  <b>Provide Services</b> – process bookings, issue eSIMs, complete
                  payments, and manage your account.
                </li>
                <li>
                  <b>Ensure Legal & Regulatory Compliance</b> – meet telecom,
                  travel, and anti-fraud requirements.
                </li>
                <li>
                  <b>Communicate with You</b> – send confirmations, receipts,
                  updates, and respond to inquiries.
                </li>
                <li>
                  <b>Personalize Experiences</b> – tailor recommendations,
                  promotions, and content to your preferences.
                </li>
                <li>
                  <b>Improve & Secure Services</b> – monitor performance, resolve
                  issues, and prevent fraud or misuse.
                </li>
                <li>
                  <b>Marketing (where permitted)</b> – deliver promotions, surveys,
                  and special offers with your consent or where legally allowed.
                </li>
              </ol>

              <hr />

              {/* 5. How We Share Your Information */}
              <h2>5. How We Share Your Information</h2>
              <p>We may share your personal data with:</p>
              <ul>
                <li>
                  <b>Service Providers</b> – airlines, hotels, telecom operators,
                  payment processors, verification partners.
                </li>
                <li>
                  <b>Affiliates & Subsidiaries</b> – within the Teleport Now group for
                  operations and marketing.
                </li>
                <li>
                  <b>Regulators & Authorities</b> – when required by law (e.g.,
                  immigration, customs, telecom regulators).
                </li>
                <li>
                  <b>Third Parties</b> – trusted partners for analytics, marketing,
                  and customer feedback.
                </li>
                <li>
                  <b>Business Transactions</b> – in case of mergers, acquisitions,
                  restructuring, or asset transfers.
                </li>
              </ul>
              <p>
                <b>We do not sell your personal data to third parties.</b>
              </p>

              <hr />

              {/* 6. International Data Transfers */}
              <h2>6. International Data Transfers</h2>
              <p>
                Your data may be transferred to and stored in countries outside your
                place of residence. We implement safeguards such as encryption,
                restricted access, and contractual protections to ensure your data
                is handled securely.
              </p>

              <hr />

              {/* 7. Data Retention */}
              <h2>7. Data Retention</h2>
              <p>We retain personal data only as long as necessary to:</p>
              <ul>
                <li>Provide Services.</li>
                <li>Fulfill legal or regulatory obligations.</li>
                <li>Resolve disputes or enforce agreements.</li>
              </ul>
              <p>When data is no longer needed, we securely delete or anonymize it.</p>

              <hr />

              {/* 8. Security */}
              <h2>8. Security</h2>
              <p>We apply organizational, technical, and physical safeguards including:</p>
              <ul>
                <li>Encryption of sensitive data in transit and at rest.</li>
                <li>Restricted access to authorized personnel only.</li>
                <li>Ongoing monitoring and audits.</li>
                <li>Adherence to international security standards.</li>
              </ul>
              <p>
                However, no system is completely secure, and we cannot guarantee
                absolute protection.
              </p>

              <hr />

              {/* 9. Cookies & Tracking */}
              <h2>9. Cookies & Tracking</h2>
              <p>We use cookies, SDKs, and tracking technologies to:</p>
              <ul>
                <li>Enable essential functions.</li>
                <li>Analyze usage and performance.</li>
                <li>Provide relevant promotions and advertising.</li>
              </ul>
              <p>
                You may manage cookie preferences through your browser or device
                settings. Note that disabling certain cookies may impact
                functionality.
              </p>

              <hr />

              {/* 10. Your Rights */}
              <h2>10. Your Rights</h2>
              <p>Depending on your jurisdiction, you may have the right to:</p>
              <ul>
                <li>Access the data we hold about you.</li>
                <li>Request corrections or deletion.</li>
                <li>Restrict or object to processing.</li>
                <li>Withdraw consent at any time.</li>
                <li>Opt out of marketing.</li>
                <li>Request data portability.</li>
                <li>File a complaint with your local Data Protection Authority.</li>
              </ul>

              <hr />

              {/* 11. Children’s Data */}
              <h2>11. Children’s Data</h2>
              <p>
                Our Services are not directed at children under 13, and we do not
                knowingly collect their data. If you believe a child has provided
                personal information, contact us and we will delete it.
              </p>

              <hr />

              {/* 12. Changes to this Policy */}
              <h2>12. Changes to this Policy</h2>
              <p>
                We may update this Policy to reflect changes in practices or legal
                requirements. Significant updates will be communicated via our app,
                website, or email.
              </p>

              <hr />

              {/* 13. Contact Us */}
              <h2>13. Contact Us</h2>
              <p>
                <b>Teleport Now (SG) Pte. Ltd.</b> <br />
                60 Paya Lebar Road, #06-28 Paya Lebar Square, Singapore 409051{" "}
                <br />
                <b>Email:</b> legal@teleportapp.sg
              </p>
            </div>
          </div>
        </div>
      </div>
    <Footer />
    </>
  );
};

export default PrivacyPolicy;
